export type Agent = {
  id: string;
  name: string;
  tagline: string;
  description: string;
  iconBg: string;
  heroBg: string;
  category: "featured" | "finance" | "food" | "services";
  isOfficial: boolean;
  smsPhone: string;
  initialMessage: string;
  conversations: {
    userMessage: string;
    agentResponse: string;
    agentDetail?: string;
  }[];
};

/**
 * Agent definitions for the store.
 * IMPORTANT: When adding/removing agents, also update AGENT_IDS in src/middleware.ts
 * to ensure the corresponding routes are publicly accessible.
 */
export const agents: Agent[] = [
  {
    id: "passage-money",
    name: "Passage Money",
    tagline: "Your AI financial advisor",
    description:
      "Track spending, analyze transactions, and get personalized money insights via text. Connect your bank accounts and get real-time answers about your finances.",
    iconBg: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
    heroBg: "linear-gradient(135deg, #5b21b6 0%, #7c3aed 40%, #a78bfa 100%)",
    category: "finance",
    isOfficial: true,
    smsPhone: "+14158080461",
    initialMessage: "I want to talk to my money",
    conversations: [
      {
        userMessage: "How much did I spend on food this week?",
        agentResponse:
          "You spent $127.43 on food this week across 8 transactions.",
        agentDetail: "That's 12% less than last week.",
      },
      {
        userMessage: "What's my biggest expense this month?",
        agentResponse: "Your biggest expense is Rent at $2,100.",
        agentDetail: "Following that: Groceries ($340) and Dining ($215).",
      },
    ],
  },
  {
    id: "ubereats",
    name: "UberEats",
    tagline: "View your personalized deals",
    description:
      "Order from your favorite restaurants and track delivery through iMessage. Get personalized deals and recommendations based on your order history.",
    iconBg: "linear-gradient(135deg, #000000 0%, #333333 100%)",
    heroBg: "linear-gradient(135deg, #1a1a1a 0%, #374151 50%, #6b7280 100%)",
    category: "food",
    isOfficial: false,
    smsPhone: "+14158080461",
    initialMessage: "Show me my UberEats deals",
    conversations: [
      {
        userMessage: "What deals do I have?",
        agentResponse: "You have 3 personalized deals available!",
        agentDetail:
          "$5 off Chipotle, Free delivery on Sweetgreen, 20% off Panda Express.",
      },
      {
        userMessage: "Order my usual from Chipotle",
        agentResponse:
          "Got it! Ordering your usual: Chicken Burrito Bowl with guac, no beans.",
        agentDetail: "Estimated delivery: 25-35 min. Total: $14.50.",
      },
    ],
  },
  {
    id: "doordash",
    name: "DoorDash",
    tagline: "View your personalized deals",
    description:
      "Find restaurants, place orders, and track delivery via text message. Get exclusive DashPass deals and personalized recommendations.",
    iconBg: "linear-gradient(135deg, #FF3008 0%, #C41200 100%)",
    heroBg: "linear-gradient(135deg, #991b1b 0%, #dc2626 40%, #f87171 100%)",
    category: "food",
    isOfficial: false,
    smsPhone: "+14158080461",
    initialMessage: "Show me my DoorDash deals",
    conversations: [
      {
        userMessage: "Show me my deals",
        agentResponse: "You have 4 DashPass deals available!",
        agentDetail:
          "Free delivery on orders $15+, $3 off McDonald's, and more.",
      },
      {
        userMessage: "What's open near me right now?",
        agentResponse: "Found 12 restaurants delivering to you now.",
        agentDetail:
          "Top picks: Panda Express (20 min), Chick-fil-A (25 min), Five Guys (30 min).",
      },
    ],
  },
  {
    id: "sweetgreen",
    name: "Sweetgreen",
    tagline: "View your rewards and offers",
    description:
      "Browse the menu, customize your bowl, and place orders through iMessage. Check your rewards balance and get personalized offers.",
    iconBg: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
    heroBg: "linear-gradient(135deg, #166534 0%, #22c55e 40%, #86efac 100%)",
    category: "food",
    isOfficial: false,
    smsPhone: "+14158080461",
    initialMessage: "Show me my Sweetgreen rewards",
    conversations: [
      {
        userMessage: "What's my rewards balance?",
        agentResponse: "You have 450 points - that's a free bowl!",
        agentDetail: "Redeem on your next order or keep earning.",
      },
      {
        userMessage: "Create a custom bowl with kale and chicken",
        agentResponse:
          "Building your bowl! Kale base + Blackened Chicken added.",
        agentDetail: "Add toppings? Avocado, sweet potato, goat cheese?",
      },
    ],
  },
  {
    id: "perpay",
    name: "Perpay",
    tagline: "Shop now, pay over time",
    description:
      "Browse products, check credit limits, and manage payments via text. Shop from top brands and pay through automatic paycheck deductions.",
    iconBg: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
    heroBg: "linear-gradient(135deg, #1e40af 0%, #3b82f6 40%, #93c5fd 100%)",
    category: "services",
    isOfficial: false,
    smsPhone: "+14158080461",
    initialMessage: "Check my Perpay credit",
    conversations: [
      {
        userMessage: "What's my available credit?",
        agentResponse: "Your available credit is $450.",
        agentDetail: "Current balance: $150. Next payment due March 15th.",
      },
      {
        userMessage: "Show me AirPods",
        agentResponse: "AirPods Pro (2nd Gen) - $249",
        agentDetail: "Pay $24.90/mo for 10 months. Want to add to cart?",
      },
    ],
  },
];

export const agentsById = agents.reduce(
  (acc, agent) => {
    acc[agent.id] = agent;
    return acc;
  },
  {} as Record<string, Agent>,
);

export const LOGO_URLS: Record<string, string> = {
  ubereats:
    "https://cdn.dribbble.com/userupload/27193241/file/original-b7aa7440a5ce26ae370b4ba9f2222bf4.jpg",
  doordash: "https://companieslogo.com/img/orig/DASH-4b1e7048.png?t=1720244491",
  sweetgreen:
    "https://play-lh.googleusercontent.com/p3BSzfGRMU-HfxFH6V7Qb_abjyOzBSJlPkfKghnJgqXHF7FzJb2heotot1uP7h0Gvr0",
  perpay:
    "https://play-lh.googleusercontent.com/TO7Q7axa4sK3HV6qHeFT8VfaPxAeNeYiOCo7gxYaMeu9WaNWSy6QfmcIG-gxPBxLk54=w240-h480-rw",
};

export const categories = [
  { id: "featured", label: "Featured" },
  { id: "finance", label: "Finance" },
  { id: "food", label: "Food & Dining" },
  { id: "services", label: "Services" },
];
